<template>
  <div class="cws-container blog-wrapper">
    <div>
      <b-row>
        <b-col md="12" lg="12">
          <b-card
              title="Проверка Пакета"
              class="mb-3"
              border-variant="primary"
          >
            <b-card-text>
              Загрузка SIQ-файла для проверки корректности пакета
            </b-card-text>

            <b-form-group
                label="SIQ-Файл"
                label-for="siq-file"
            >
              <b-button
                  variant="outline-primary"
                  @click="$refs.refInputUploadFile.click()"
              >
                <input
                    ref="refInputUploadFile"
                    type="file"
                    class="d-none"
                    id="siq-file"
                    @input="inputSiqFileRenderer"
                />
                <span class="d-none d-sm-inline">Upload</span>
                <feather-icon
                    icon="UploadIcon"
                    class="d-inline d-sm-none"
                />
              </b-button>
            </b-form-group>
          </b-card>
        </b-col>
      </b-row>
    </div>

    <!-- content -->
    <div class="blog-detail-wrapper">
      <b-row v-if="validationData">
        <b-col cols="12">
          <b-overlay
              :show="isLoading"
              rounded="sm"
          >
            <b-card>
              <h2 class="mb-1">{{ validationData.title }}</h2>
              <p class="text-muted mb-2">
                Автор: <strong>{{ validationData.author }}</strong> |
                Раундов: <strong>{{ roundsCount }}</strong> |
                Вопросов: <strong>{{ itemsCount }}</strong>
              </p>

              <!-- Общие параметры пакета -->
              <b-row v-if="summaryStats.length" class="mb-2">
                <b-col
                    v-for="stat in summaryStats"
                    :key="stat.label"
                    md="3"
                    sm="6"
                    cols="12"
                    class="mb-1"
                >
                  <div class="border rounded p-1 text-center h-100">
                    <div class="text-muted small">{{ stat.label }}</div>
                    <!-- Пока нейтрально-зелёное, без порогов; место под будущие ворнинги -->
                    <div class="text-success h4 mb-0">{{ stat.value }}</div>
                  </div>
                </b-col>
              </b-row>

              <!-- Формат стоимостей -->
              <div v-if="validationData.cost_format" class="mb-2">
                <h5 class="mb-1">
                  Формат стоимостей:
                  <b-badge variant="light-primary">{{ validationData.cost_format.scheme_label }}</b-badge>
                </h5>
                <b-table-simple responsive bordered small>
                  <b-thead>
                    <b-tr>
                      <b-th>Тема</b-th>
                      <b-th>Стоимости</b-th>
                      <b-th>Проблема</b-th>
                    </b-tr>
                  </b-thead>
                  <b-tbody>
                    <template v-for="(round, ri) in validationData.cost_format.rounds">
                      <b-tr :key="`cr-${ri}`" variant="light">
                        <b-th colspan="3">
                          {{ round.name }}
                          <b-badge :variant="roundCategoryVariant(round.category)">{{ roundCategoryLabel(round.category) }}</b-badge>
                        </b-th>
                      </b-tr>
                      <b-tr
                          v-for="(theme, ti) in round.themes"
                          :key="`cr-${ri}-t-${ti}`"
                          :variant="costRowVariant(theme.status)"
                      >
                        <b-td>{{ theme.name }}</b-td>
                        <b-td>{{ theme.prices.join('-') || '—' }}</b-td>
                        <b-td class="small">{{ theme.message }}</b-td>
                      </b-tr>
                    </template>
                  </b-tbody>
                </b-table-simple>
              </div>

              <!-- Главная таблица по вопросам -->
              <b-table-simple responsive bordered small class="mt-1" v-if="validationData.items">
                <b-thead>
                  <b-tr>
                    <b-th>№</b-th>
                    <b-th>Артист (файл)</b-th>
                    <b-th>Артист (Spotify)</b-th>
                    <b-th>Трек</b-th>
                    <b-th>Контент</b-th>
                  </b-tr>
                </b-thead>
                <b-tbody>
                  <b-tr
                      v-for="(item, itemIndex) in validationData.items"
                      :key="itemIndex"
                      :variant="rowIsRed(item) ? 'danger' : null"
                  >
                    <b-td>
                      {{ item.round_index + 1 }}.{{ item.theme_index + 1 }}.{{ item.question_index }}
                      <b-badge v-if="item.special_type === 'auction'" variant="light-info">аукцион</b-badge>
                      <b-badge v-if="item.special_type === 'cat'" variant="light-info">кот</b-badge>
                    </b-td>

                    <!-- Артист (файл) -->
                    <b-td>
                      <div v-for="(artist, artistIndex) in item.artists" :key="artistIndex">
                        <b-link
                            v-if="artist.db_id"
                            :to="{ name: 'artist-view', params: { id: artist.db_id } }"
                            :class="artistClass(artist)"
                        >
                          {{ artist.name }}
                        </b-link>
                        <span v-else :class="artistClass(artist)">{{ artist.name }}</span>
                        <feather-icon
                            v-if="artistState(artist) === 'ok'"
                            icon="CheckIcon"
                            size="14"
                            class="text-success"
                        />
                        <!-- Регистр отличается — убираемый бэдж -->
                        <template v-if="showCaseMismatch(artist, itemIndex, artistIndex)">
                          <b-badge variant="light-secondary" title="Регистр отличается">Аа</b-badge>
                          <b-button
                              size="sm"
                              variant="flat-success"
                              class="p-25"
                              @click="dismissCaseMismatch(artistKey(itemIndex, artistIndex))"
                          >
                            <feather-icon icon="CheckIcon" size="14" />
                          </b-button>
                        </template>
                        <!-- В БД есть привязка к Spotify — заметка, убираемая -->
                        <template v-if="showNote(artist, itemIndex, artistIndex)">
                          <b-badge variant="light-info" title="В БД есть привязка к Spotify">
                            <feather-icon icon="FileTextIcon" size="12" />
                          </b-badge>
                          <b-button
                              size="sm"
                              variant="flat-success"
                              class="p-25"
                              @click="dismissNote(artistKey(itemIndex, artistIndex))"
                          >
                            <feather-icon icon="CheckIcon" size="14" />
                          </b-button>
                        </template>
                        <b-button
                            v-if="artistState(artist) === 'warning' && !dismissedArtists.has(artistKey(itemIndex, artistIndex))"
                            size="sm"
                            variant="flat-success"
                            class="p-25"
                            @click="dismissArtist(artistKey(itemIndex, artistIndex))"
                        >
                          <feather-icon icon="CheckIcon" size="14" />
                        </b-button>
                      </div>
                      <span v-if="!item.artists.length" class="text-danger">— нет артиста —</span>
                    </b-td>

                    <!-- Артист (Spotify) -->
                    <b-td>
                      <div v-for="(artist, artistIndex) in item.artists" :key="artistIndex">
                        <b-spinner v-if="artistState(artist) === 'loading'" small />
                        <template v-else-if="artistSpotify(artist) && artistSpotify(artist).spotify">
                          {{ artistSpotify(artist).spotify.name }}
                          <b-link
                              :href="spotifyArtistUrl(artistSpotify(artist).spotify.id)"
                              target="_blank"
                              rel="noopener"
                              title="Открыть в Spotify (популярность)"
                          >
                            <b-badge variant="light-primary">{{ artistSpotify(artist).spotify.popularity }}</b-badge>
                          </b-link>
                        </template>
                        <template v-else-if="artistSpotify(artist) && artistSpotify(artist).candidates && artistSpotify(artist).candidates.length">
                          <div
                              v-for="cand in artistSpotify(artist).candidates"
                              :key="cand.id"
                              class="small text-muted"
                          >
                            {{ cand.name }}
                            <b-link
                                :href="spotifyArtistUrl(cand.id)"
                                target="_blank"
                                rel="noopener"
                                title="Открыть в Spotify (популярность)"
                            >
                              <b-badge variant="light-secondary">{{ cand.popularity }}</b-badge>
                            </b-link>
                          </div>
                        </template>
                        <span v-else class="text-danger small">—</span>
                      </div>
                    </b-td>

                    <b-td>{{ item.track_title }}</b-td>

                    <!-- Контент -->
                    <b-td>
                      <span :class="contentClass(item, itemIndex)">
                        {{ contentIcon(item.content.kind) }}
                        <template v-if="item.content.size_bytes">{{ Math.round(item.content.size_bytes / 1024) }} КБ</template>
                      </span>
                      <b-button
                          v-if="contentHasWarning(item, itemIndex)"
                          size="sm"
                          variant="flat-success"
                          class="p-25"
                          @click="dismissContent(itemIndex)"
                      >
                        <feather-icon icon="CheckIcon" size="14" />
                      </b-button>
                      <!-- Причины ворнинга — под красной подсветкой -->
                      <div v-if="contentHasWarning(item, itemIndex)" class="small text-danger mt-25">
                        <div v-for="reason in contentWarningReasons(item)" :key="reason">{{ reason }}</div>
                      </div>
                    </b-td>
                  </b-tr>
                </b-tbody>
              </b-table-simple>
            </b-card>
          </b-overlay>
        </b-col>
      </b-row>

      <b-row v-else>
        <b-col cols="12">
          <b-overlay
              :show="isLoading"
              rounded="sm"
          >
            <b-card
                title="Данных нет"
                class="col-md-12 col-12"
            >
              <b-card-text>
                Загрузите SIQ-файл для проверки
              </b-card-text>
            </b-card>
          </b-overlay>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
import {
  BFormGroup,
  BCard,
  BRow,
  BCol,
  BCardText,
  BButton,
  BOverlay,
  BTableSimple,
  BThead,
  BTbody,
  BTr,
  BTh,
  BTd,
  BBadge,
  BLink,
  BSpinner,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import store from "@/store";
import musicStoreModule from "@/views/db-music/musicStoreModule";
import {onUnmounted, ref, computed} from "@vue/composition-api";
import {useToast} from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import {useInputImageRenderer} from "@core/comp-functions/forms/form-utils";

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BFormGroup,
    BCardText,
    BButton,
    BOverlay,
    BTableSimple,
    BThead,
    BTbody,
    BTr,
    BTh,
    BTd,
    BBadge,
    BLink,
    BSpinner,
  },
  directives: {
    Ripple,
  },
  setup() {
    const STORE_MODULE_NAME = 'playlist'
    const toast = useToast()

    // Register module
    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, musicStoreModule)

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME)
    })

    const validationData = ref(null)
    const isLoading = ref(false)
    const refInputUploadFile = ref(null)

    const roundsCount = computed(() => (validationData.value && validationData.value.rounds ? validationData.value.rounds.length : 0))
    const itemsCount = computed(() => (validationData.value && validationData.value.items ? validationData.value.items.length : 0))

    // Плитки «Общие параметры пакета» — пока всё нейтрально-зелёное, без порогов
    const summaryStats = computed(() => {
      if (!validationData.value || !validationData.value.summary) return []
      const summary = validationData.value.summary
      return [
        {label: 'Больших раундов', value: summary.rounds_big},
        {label: 'Блиц-раундов', value: summary.rounds_blitz},
        {label: 'Финал', value: summary.final_round_present ? 'Есть' : 'Нет'},
        {label: 'Тем в финале', value: summary.themes_in_final},
        {label: 'Вопросов в теме (Большие)', value: (summary.questions_per_theme_big || []).join(', ') || '—'},
        {label: 'Вопросов в теме (Блиц)', value: (summary.questions_per_theme_blitz || []).join(', ') || '—'},
        {label: 'Аукционов', value: summary.auctions_total},
        {label: 'Котов в мешке', value: summary.cats_total},
      ]
    })

    // === Проверка артистов по Spotify (прогрессивная догрузка + раскраска) ===

    // Реактивная мапа normName(artist.name) -> {loading} | {matched, case_mismatch, spotify, candidates, error}
    const spotifyByName = ref({})
    // Счётчик сессии — защита от гонки при повторной загрузке пакета
    const enrichSession = ref(0)
    // Сброшенные ворнинги/бэджи — только в состоянии страницы, никуда не пишем
    const dismissedArtists = ref(new Set())
    const dismissedContent = ref(new Set())
    const dismissedCaseMismatch = ref(new Set())
    const dismissedNotes = ref(new Set())

    const normName = name => (name || '').trim().replace(/\s+/g, ' ').toLowerCase()

    const artistSpotify = artist => spotifyByName.value[normName(artist.name)] || null

    const artistState = artist => {
      const sp = artistSpotify(artist)
      if (!sp || sp.loading) return 'loading'
      if (sp.matched === true) return 'ok'
      // В БД есть привязка к Spotify — доверяем связке, помечаем зелёным
      if (artist.db_id && artist.db_spotify_id) return 'ok'
      return artist.db_id ? 'warning' : 'bad'
    }

    const spotifyArtistUrl = id => `https://open.spotify.com/artist/${id}`

    const artistClass = artist => ({
      loading: 'text-muted',
      ok: 'text-success font-weight-bold',
      warning: 'text-warning font-weight-bold',
      bad: 'text-danger font-weight-bold',
    }[artistState(artist)])

    const caseMismatch = artist => {
      const sp = artistSpotify(artist)
      return !!(sp && sp.case_mismatch)
    }

    const artistKey = (itemIndex, artistIndex) => `${itemIndex}:${artistIndex}`

    const dismissArtist = key => {
      const next = new Set(dismissedArtists.value)
      next.add(key)
      dismissedArtists.value = next
    }

    // Бэдж «регистр отличается» — показываем, пока не сброшен галочкой
    const showCaseMismatch = (artist, itemIndex, artistIndex) => caseMismatch(artist)
      && !dismissedCaseMismatch.value.has(artistKey(itemIndex, artistIndex))

    const dismissCaseMismatch = key => {
      const next = new Set(dismissedCaseMismatch.value)
      next.add(key)
      dismissedCaseMismatch.value = next
    }

    // Бэдж-заметка «зелёный из-за привязки в БД»: показываем только если в БД есть
    // Spotify-привязка И имя НЕ совпало по Spotify (иначе совпадение и так green), пока не сброшен галочкой
    const showNote = (artist, itemIndex, artistIndex) => {
      const sp = artistSpotify(artist)
      return !!(artist.db_id && artist.db_spotify_id && sp && !sp.loading && sp.matched !== true)
        && !dismissedNotes.value.has(artistKey(itemIndex, artistIndex))
    }

    const dismissNote = key => {
      const next = new Set(dismissedNotes.value)
      next.add(key)
      dismissedNotes.value = next
    }

    const rowIsRed = item => !item.answer_raw || item.artists.length === 0

    const contentIcon = kind => ({image: '🖼', audio: '🎵', video: '🎬', text: '📝', none: ''}[kind] || '')

    const contentHasWarning = (item, itemIndex) => item.content.warnings.length > 0 && !dismissedContent.value.has(itemIndex)

    const contentClass = (item, itemIndex) => (contentHasWarning(item, itemIndex) ? 'text-danger font-weight-bold' : '')

    // Человекочитаемые причины контент-ворнинга
    const CONTENT_WARNING_LABELS = {size: 'Больше 200 КБ', png: 'Формат PNG', webp: 'Формат WEBP'}
    const contentWarningReasons = item => (item.content.warnings || []).map(code => CONTENT_WARNING_LABELS[code] || code)

    // Формат стоимостей
    const roundCategoryLabel = category => ({big: 'Большой', blitz: 'Блиц', final: 'Финал'}[category] || category)
    const roundCategoryVariant = category => ({big: 'light-primary', blitz: 'light-warning', final: 'light-secondary'}[category] || 'light-secondary')
    const costRowVariant = status => ({error: 'danger', warning: 'warning'}[status] || null)

    const dismissContent = itemIndex => {
      const next = new Set(dismissedContent.value)
      next.add(itemIndex)
      dismissedContent.value = next
    }

    // Прогрессивная догрузка Spotify: уникальные артисты, чанки по 15, последовательно
    function enrichSpotify(items) {
      const session = enrichSession.value
      const uniqueMap = new Map()
      items.forEach(item => {
        (item.artists || []).forEach(artist => {
          const key = normName(artist.name)
          if (key && !uniqueMap.has(key)) {
            uniqueMap.set(key, {name: artist.name, spotify_id: artist.db_spotify_id || undefined})
          }
        })
      })
      const uniqueArtists = Array.from(uniqueMap.values())
      if (!uniqueArtists.length) return

      // Помечаем всех как loading
      const loadingPatch = {}
      uniqueArtists.forEach(a => {
        loadingPatch[normName(a.name)] = {loading: true}
      })
      spotifyByName.value = {...spotifyByName.value, ...loadingPatch}

      // Разбиваем на чанки по 15
      const chunkSize = 15
      const chunks = []
      for (let i = 0; i < uniqueArtists.length; i += chunkSize) {
        chunks.push(uniqueArtists.slice(i, i + chunkSize))
      }

      // Обрабатываем чанки последовательно (reduce-цепочка промисов)
      chunks.reduce((chain, chunk) => chain.then(() => {
        if (session !== enrichSession.value) return undefined // устаревшая загрузка — не пишем
        return store.dispatch('playlist/spotifyValidateArtists', {artists: chunk})
            .then(res => {
              if (session !== enrichSession.value) return
              const patch = {}
              ;(res.data || []).forEach(entry => {
                patch[normName(entry.name)] = {loading: false, ...entry}
              })
              spotifyByName.value = {...spotifyByName.value, ...patch}
            })
            .catch(() => {
              if (session !== enrichSession.value) return
              const patch = {}
              chunk.forEach(a => {
                patch[normName(a.name)] = {loading: false, error: true}
              })
              spotifyByName.value = {...spotifyByName.value, ...patch}
            })
      }), Promise.resolve())
    }

    const {inputImageRenderer: inputSiqFileRenderer} = useInputImageRenderer(refInputUploadFile, async base64 => {
      const file = refInputUploadFile.value.files[0]
      enrichSession.value += 1
      isLoading.value = true
      validationData.value = null
      spotifyByName.value = {}
      dismissedArtists.value = new Set()
      dismissedContent.value = new Set()
      dismissedCaseMismatch.value = new Set()
      dismissedNotes.value = new Set()
      try {
        const res = await store.dispatch('playlist/sigameValidate', {file: file})
        validationData.value = res.data
        if (res.data && res.data.items) {
          enrichSpotify(res.data.items)
        }
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка загрузки файла',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isLoading.value = false
      }
    })

    return {
      validationData,
      isLoading,
      inputSiqFileRenderer,
      refInputUploadFile,
      roundsCount,
      itemsCount,
      summaryStats,
      spotifyByName,
      dismissedArtists,
      dismissedContent,
      artistSpotify,
      artistState,
      artistClass,
      caseMismatch,
      artistKey,
      dismissArtist,
      spotifyArtistUrl,
      showCaseMismatch,
      dismissCaseMismatch,
      showNote,
      dismissNote,
      rowIsRed,
      contentIcon,
      contentHasWarning,
      contentClass,
      contentWarningReasons,
      dismissContent,
      roundCategoryLabel,
      roundCategoryVariant,
      costRowVariant,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/pages/page-blog.scss';
</style>
